import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown, Phone, Mail, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'

const navigation = [
  { name: 'Home', href: '/' },
  {
    name: 'About',
    href: '/about',
    children: [
      { name: 'Our Story', href: '/about#story' },
      { name: 'Chairman Message', href: '/about#chairman' },
      { name: 'Vision & Mission', href: '/about#vision' },
      { name: 'Core Values', href: '/about#values' },
    ]
  },
  {
    name: 'Services',
    href: '/services',
    children: [
      { name: 'Furniture Solutions', href: '/services/furniture' },
      { name: 'Smart Education', href: '/services/smart-education' },
      { name: 'Digital Transformation', href: '/services/digital' },
      { name: 'Healthcare Solutions', href: '/services/healthcare' },
      { name: 'Infrastructure', href: '/services/infrastructure' },
      { name: 'Security Solutions', href: '/services/security' },
    ]
  },
  { name: 'Group Companies', href: '/group-companies' },
  { name: 'Projects', href: '/projects' },
  { name: 'Industries', href: '/industries' },
  { name: 'Careers', href: '/careers' },
  { name: 'Contact', href: '/contact' },
]

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [location])

  return (
    <>
      {/* Top Bar */}
      <div className="hidden lg:block bg-primary-900/90 backdrop-blur-sm border-b border-white/5">
        <div className="container-premium">
          <div className="flex items-center justify-between py-2 text-sm">
            <div className="flex items-center gap-6">
              <a href="tel:+919876543210" className="flex items-center gap-2 text-graphite-400 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                <span>+91 98765 43210</span>
              </a>
              <a href="mailto:info@gsmcorp.com" className="flex items-center gap-2 text-graphite-400 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                <span>info@gsmcorp.com</span>
              </a>
              <div className="flex items-center gap-2 text-graphite-400">
                <MapPin className="w-4 h-4" />
                <span>Maharashtra, India</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/franchise" className="text-accent-400 hover:text-accent-300 font-medium">
                Become a Partner
              </Link>
              <Link to="/careers" className="text-secondary-400 hover:text-secondary-300 font-medium">
                Careers
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={cn(
          "sticky top-0 z-50 transition-all duration-500",
          scrolled
            ? "bg-graphite-950/95 backdrop-blur-xl shadow-lg shadow-black/20"
            : "bg-transparent"
        )}
      >
        <div className="container-premium">
          <nav className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg blur-lg opacity-50 group-hover:opacity-100 transition-opacity" />
                <div className="relative w-12 h-12 bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-display font-bold text-xl">GSM</span>
                </div>
              </div>
              <div className="hidden md:block">
                <h1 className="text-white font-display font-bold text-xl tracking-wide">GSM CORPORATION</h1>
                <p className="text-xs text-graphite-500 tracking-widest uppercase">Transforming India</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => item.children && setActiveDropdown(item.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center gap-1 px-4 py-2 text-base font-medium transition-all duration-300 rounded-lg",
                      location.pathname === item.href
                        ? "text-primary-400 bg-primary-500/10"
                        : "text-graphite-300 hover:text-white hover:bg-white/5"
                    )}
                  >
                    {item.name}
                    {item.children && <ChevronDown className="w-4 h-4" />}
                  </Link>

                  {/* Dropdown */}
                  {item.children && (
                    <AnimatePresence>
                      {activeDropdown === item.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 pt-2 w-56"
                        >
                          <div className="bg-graphite-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                            {item.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.href}
                                className="block px-4 py-3 text-graphite-300 hover:text-white hover:bg-white/5 transition-colors"
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Button variant="outline" size="sm" asChild>
                <Link to="/franchise">Partner With Us</Link>
              </Button>
              <Button variant="gold" size="sm" asChild>
                <Link to="/contact">Request Proposal</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-graphite-950/98 backdrop-blur-xl border-t border-white/5"
            >
              <div className="container-premium py-4">
                <div className="flex flex-col gap-2">
                  {navigation.map((item) => (
                    <div key={item.name}>
                      <Link
                        to={item.href}
                        className={cn(
                          "block py-3 px-4 text-lg font-medium rounded-lg transition-colors",
                          location.pathname === item.href
                            ? "text-primary-400 bg-primary-500/10"
                            : "text-graphite-300 hover:text-white hover:bg-white/5"
                        )}
                      >
                        {item.name}
                      </Link>
                      {item.children && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              className="block py-2 px-4 text-graphite-400 hover:text-white transition-colors"
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-white/10 flex flex-col gap-3">
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/franchise">Partner With Us</Link>
                  </Button>
                  <Button variant="gold" size="sm" asChild>
                    <Link to="/contact">Request Proposal</Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  )
}